import type { CoachJourneyInput, CoachTransitionInput } from '../schema';

// Keep the conversation visible and usable while the person tries each prompt.
const INPUT = [
  { anchor: 'copilot.input' as const, placement: 'left' as const, spotlight: false },
  {
    anchor: 'toolbar.copilot' as const,
    title: 'Open your creative sidekick ✨',
    body: 'Click Copilot to pick up your conversation. Your board and progress are still here.',
    spotlight: false,
  },
];

const DRAFT_MADE: CoachTransitionInput = {
  event: 'node.added',
  match: ['boardId'],
  if: { nodeType: 'textNode' },
  bindAs: { nodeId: 'draftNodeId' },
  fresh: true,
  to: 'draft-ready',
};

const IMAGE_MADE: CoachTransitionInput = {
  event: 'node.added',
  match: ['boardId'],
  if: { nodeType: 'imageNode' },
  bindAs: { nodeId: 'imageNodeId' },
  fresh: true,
  to: 'painting',
};

const SAMPLE =
  'I run a neighborhood bakery. My audience is busy locals who want a good breakfast. ' +
  'I want an Instagram post about our Saturday cinnamon rolls. My voice is warm, playful, ' +
  'and specific. The goal is to get people to visit this Saturday. Do not invent prices or offers.';

function conversationSteps(steps: CoachJourneyInput['steps']): CoachJourneyInput['steps'] {
  const recovery: Record<string, string> = {
    'thinking-angles': 'angles-review',
    writing: 'draft-help',
    revising: 'revision-review',
    researching: 'research-review',
    'making-image': 'image-help',
  };
  return steps.map(step => ({
    ...step,
    display: step.display ?? 'conversation',
    ...(recovery[step.id]
      ? {
          on: [
            ...(step.on ?? []),
            { event: 'copilot.failed' as const, match: ['boardId'], to: recovery[step.id]! },
          ],
        }
      : {}),
  }));
}

/**
 * A general Copilot playground, separate from the WBBC challenge. The only setup is one
 * local note; paid prompts are filled for the person to edit and send. Real board events
 * reveal the draft and image, with recovery paths when a reply makes neither. Discovery and
 * new starts follow the web app's Tutorials rollout alongside the other new-board lessons.
 */
export const copilotJourney: CoachJourneyInput = {
  id: 'copilot-playground',
  version: 1,
  name: 'Meet Poppy Copilot',
  description: 'Turn your own brief into ideas, a draft, and a fresh creative direction.',
  mode: 'standard',
  start: { link: true },
  tutorial: {
    description: 'Make something real. Learn board context, drafts, edits, research, and visuals.',
    on: 'new-board',
  },
  steps: conversationSteps([
    {
      id: 'open',
      display: 'hidden',
      title: 'Opening your playground ✨',
      body: 'Open a board you can edit to get started.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'welcome' }],
    },
    {
      id: 'welcome',
      chapter: 1,
      title: 'Your ideas. A little Poppy magic ✨',
      body: 'Let’s make something for YOU. Give Poppy a brief, turn it into a draft, then shape it together.\n\nThis is your practice board to keep. You choose which prompts to send. Replies and generated visuals use your usual Poppy credits.',
      action: { label: 'Let’s make something', to: 'brief' },
    },
    {
      id: 'brief',
      chapter: 1,
      title: 'What are you making? 🎯',
      body: 'Tell me what you do, who you want to reach, and what you want them to do next. A few messy sentences are perfect.',
      form: {
        id: 'text',
        bind: 'brief',
        placeholder:
          'I’m a fitness coach. I help busy parents. I want a post about a 10-minute workout…',
        submit: 'Make it my brief',
      },
      do: {
        action: 'board.add-note',
        params: { title: 'Your creative brief', textFrom: 'brief' },
        bind: { nodeId: 'briefNodeId' },
        then: 'context',
      },
      action: { label: 'Try a bakery example', to: 'sample' },
    },
    {
      id: 'sample',
      display: 'hidden',
      title: 'Adding your bakery brief 🥐',
      body: 'Your first idea is coming together.',
      do: {
        action: 'board.add-note',
        params: { title: 'Your creative brief', text: SAMPLE },
        bind: { nodeId: 'briefNodeId' },
        then: 'context',
      },
    },
    {
      id: 'context',
      chapter: 1,
      title: 'Your board is the starting point 🧠',
      body: 'Here’s your brief. Copilot can read notes, links, transcripts, and other content on this board when you ask.\n\nYou don’t have to paste everything into every message. Let’s try it.',
      show: [{ node: 'briefNodeId', placement: 'right', spotlight: false }],
      focus: { node: 'briefNodeId', placement: 'right', maxZoom: 1 },
      action: { label: 'Find my first angles', to: 'angles' },
    },
    {
      id: 'angles',
      resumeAt: 'angles',
      chapter: 2,
      title: 'One brief. Three ways in 🎣',
      body: 'Your first prompt is ready. Edit anything you like, then hit Send.\n\nAsk for a reason behind each hook so you learn what makes it work.',
      show: INPUT,
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Read “Your creative brief” on this board. Suggest 3 specific hooks for a social post based on it. Explain why each would catch my audience’s attention. Use only facts from the brief. Don’t create anything on the board yet.',
        },
      },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'thinking-angles' }],
      action: { label: 'Skip to making a draft', to: 'draft' },
    },
    {
      id: 'thinking-angles',
      chapter: 2,
      display: 'toast',
      show: INPUT,
      title: 'Finding your angles 🎣',
      body: 'Poppy is reading your brief and cooking up ideas 🍳',
      resumeAt: 'angles-review',
      on: [{ event: 'copilot.replied', match: ['boardId'], to: 'angles-review' }],
      action: { label: 'Keep going', to: 'draft' },
    },
    {
      id: 'angles-review',
      chapter: 2,
      title: 'Which hook sounds like you? 👀',
      body: 'Read Poppy’s reply and pick your favorite. You can tell her what you like or answer a question right here.\n\nIf the reply stopped or hit a snag, send your message again. Ready? Let’s save a real draft to your board.',
      show: INPUT,
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'thinking-angles' }],
      action: { label: 'Make my draft', to: 'draft' },
    },
    {
      id: 'draft',
      resumeAt: 'draft',
      chapter: 3,
      title: 'From chat to something you can keep ✍️',
      body: 'Tell Poppy which hook you like, or use the prompt below. Hit Send and watch for a new note on your board.\n\nYou can keep working with that note after this tour ends.',
      show: INPUT,
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Read “Your creative brief” on this board and use the strongest hook from our conversation, if we have one. Write a short social post in my voice with a clear next step. Save the finished words as ONE new text note on this board titled “My first Copilot draft”. Don’t publish or schedule anything.',
        },
      },
      on: [DRAFT_MADE, { event: 'copilot.sent', match: ['boardId'], to: 'writing' }],
      action: { label: 'Skip to research', to: 'research' },
    },
    {
      id: 'writing',
      chapter: 3,
      display: 'toast',
      show: INPUT,
      title: 'Writing your first draft ✍️',
      body: 'Watch your board. Your draft gets its own note ✨',
      resumeAt: 'draft-help',
      on: [DRAFT_MADE, { event: 'copilot.replied', match: ['boardId'], to: 'draft-help' }],
      action: { label: 'Keep going', to: 'research' },
    },
    {
      id: 'draft-help',
      chapter: 3,
      title: 'Let’s get those words onto your board ✍️',
      body: 'If Poppy asked a question, answer it here. If the draft is still in chat, say “Save that as a text note on this board.”\n\nAlready see your saved draft? Choose Continue. You can also skip this part.',
      show: INPUT,
      on: [DRAFT_MADE, { event: 'copilot.sent', match: ['boardId'], to: 'writing' }],
      action: { label: 'Continue to research', to: 'research' },
    },
    {
      id: 'draft-ready',
      chapter: 3,
      title: 'Your words have a home 🎉',
      body: 'Here’s the note that just landed. Open it and read your draft.\n\nNow let’s see how a quick direction can change it.',
      show: [{ node: 'draftNodeId', placement: 'left', spotlight: false }],
      focus: { node: 'draftNodeId', placement: 'left', maxZoom: 1 },
      action: { label: 'Make it sound more like me', to: 'attach' },
    },
    {
      id: 'attach',
      chapter: 4,
      title: 'Point Poppy at the right thing 📎',
      body: 'Select your draft on the board to attach it to your next message. Or click +, then Choose an item.\n\nThe little attachment chip shows what you’re talking about. You can remove it with its ×.',
      show: [{ anchor: 'copilot.attach', placement: 'left', spotlight: false }, ...INPUT],
      do: { action: 'copilot.open' },
      action: { label: 'Try a revision', to: 'revise' },
    },
    {
      id: 'revise',
      resumeAt: 'revise',
      chapter: 4,
      title: 'Give direction like a creative director 🎬',
      body: 'Try the revision below, or ask for your own change. Send it when you’re ready.\n\nPoppy can update the note itself, so your latest version stays on the board.',
      show: INPUT,
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Read my saved draft {{draftNodeId}} on this board. Make the opening punchier, shorten the post by about a third, and keep my voice and the facts in “Your creative brief”. Update that same note. Don’t make another copy.',
        },
      },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'revising' }],
      action: { label: 'Skip to research', to: 'research' },
    },
    {
      id: 'revising',
      chapter: 4,
      display: 'toast',
      show: INPUT,
      title: 'Shaping your draft ✨',
      body: 'Your direction, Poppy’s pen ✍️ Check the reply and your note.',
      resumeAt: 'revision-review',
      on: [{ event: 'copilot.replied', match: ['boardId'], to: 'revision-review' }],
      action: { label: 'Keep going', to: 'research' },
    },
    {
      id: 'revision-review',
      chapter: 4,
      title: 'You get the final say ✨',
      body: 'Compare the note with what you asked for. Want another pass? Ask Copilot what to change next.\n\nWhen Copilot changes your board, its reply offers Undo. You can try a direction without committing to it forever.',
      show: [{ node: 'draftNodeId', placement: 'right', spotlight: false }, ...INPUT],
      focus: { node: 'draftNodeId', maxZoom: 1 },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'revising' }],
      action: { label: 'Find fresh inspiration', to: 'research' },
    },
    {
      id: 'research',
      resumeAt: 'research',
      chapter: 5,
      title: 'Find what’s already working 🔎',
      body: 'Copilot can find breakout posts, creators to study, and a brand’s ads. Let’s look for inspiration in your niche.\n\nFor current facts from the wider web, + also has Search the web.',
      show: INPUT,
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Read “Your creative brief” on this board. Find 3 relevant breakout posts to study for this audience. Show the original links, explain the hooks, and suggest one fresh angle for me. If you can’t find good matches, tell me what to search for instead. Don’t copy their wording.',
        },
      },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'researching' }],
      action: { label: 'Skip to visuals', to: 'visual-choice' },
    },
    {
      id: 'researching',
      chapter: 5,
      display: 'toast',
      show: INPUT,
      title: 'Finding your inspiration 🔎',
      body: 'Poppy is looking for posts worth studying 👀',
      resumeAt: 'research-review',
      on: [{ event: 'copilot.replied', match: ['boardId'], to: 'research-review' }],
      action: { label: 'Keep going', to: 'visual-choice' },
    },
    {
      id: 'research-review',
      chapter: 5,
      title: 'Study the move, make it yours 🔥',
      body: 'Open an original post from the reply. What caught your eye? Ask Poppy why the hook works, or narrow the search if the matches feel off.\n\nYou can ask her to add a favorite link to your board for later.',
      show: INPUT,
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'researching' }],
      action: { label: 'Try a visual', to: 'visual-choice' },
    },
    {
      id: 'visual-choice',
      chapter: 6,
      title: 'Want to put a picture to it? 🎨',
      body: 'Poppy can generate images from a description and save them to your board. This part uses image credits.\n\nTry one visual, or jump straight to the shortcuts.',
      choices: [
        { label: 'Make one image', to: 'image' },
        { label: 'Show me the shortcuts', to: 'history' },
      ],
    },
    {
      id: 'image',
      resumeAt: 'image',
      chapter: 6,
      title: 'Art-direct your first image 🖌️',
      body: 'The prompt is ready. Add a mood, color, or detail that feels like you, then send it.\n\nIf Poppy asks you to confirm, check the request before you choose.',
      show: INPUT,
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Read “Your creative brief” on this board. Generate one square editorial image for this campaign and add it to the board. Make the subject specific to my brief, with natural light, rich detail, and room around the subject. No lettering or logos. Just one image.',
        },
      },
      on: [IMAGE_MADE, { event: 'copilot.sent', match: ['boardId'], to: 'making-image' }],
      action: { label: 'Skip to shortcuts', to: 'history' },
    },
    {
      id: 'making-image',
      chapter: 6,
      display: 'toast',
      show: INPUT,
      title: 'Painting your idea 🖌️',
      body: 'Your visual may take a little longer. You can keep exploring ✨',
      resumeAt: 'image-help',
      on: [IMAGE_MADE, { event: 'copilot.replied', match: ['boardId'], to: 'image-help' }],
      action: { label: 'Keep going', to: 'history' },
    },
    {
      id: 'image-help',
      chapter: 6,
      title: 'Check Poppy’s reply 👀',
      body: 'She may need a detail or your confirmation before painting. Answer here to keep going, or explore the shortcuts while your image cooks.',
      show: INPUT,
      on: [IMAGE_MADE, { event: 'copilot.sent', match: ['boardId'], to: 'making-image' }],
      action: { label: 'Show shortcuts', to: 'history' },
    },
    {
      id: 'painting',
      chapter: 6,
      display: 'toast',
      show: INPUT,
      title: 'Your image card is here 🎨',
      body: 'Your image card is on the board. The picture fills in as it finishes 🖌️',
      waitFor: { nodes: ['imageNodeId'], to: 'image-review', atMost: 90 },
      resumeAt: 'image-review',
      action: { label: 'Show my card', to: 'image-review' },
    },
    {
      id: 'image-review',
      chapter: 6,
      title: 'Take a look at your visual 🎨',
      body: 'Open the image card to check it. Still painting? It can finish while you explore. If it failed, check the reply and try again when you’re ready.\n\nYou can attach a reference image to a future message and tell Poppy what to keep or change.',
      show: [{ node: 'imageNodeId', placement: 'left', spotlight: false }],
      focus: { node: 'imageNodeId', placement: 'left', maxZoom: 1 },
      action: { label: 'Show me the shortcuts', to: 'history' },
    },
    {
      id: 'history',
      chapter: 7,
      title: 'Your conversation comes with you 💬',
      body: 'Your chats opens earlier conversations on this board. New chat gives you a fresh conversation, while your board stays put.\n\nIf you opened history, use Back to chat to return to your message box.',
      show: [{ anchor: 'copilot.history', placement: 'left', spotlight: false }, ...INPUT],
      do: { action: 'copilot.open' },
      action: { label: 'What about my other apps?', to: 'apps' },
    },
    {
      id: 'apps',
      chapter: 7,
      title: 'Bring your workflow along 🔗',
      body: 'When integrations are available on your account, Your integrations shows what you can connect. Ask Poppy to use a connected app or help with a post or schedule.\n\nCheck the account, words, and timing when she asks you to approve an action. You stay in charge of what goes out.',
      show: [{ anchor: 'copilot.apps', placement: 'left', spotlight: false }, ...INPUT],
      action: { label: 'Pick my next move', to: 'next' },
    },
    {
      id: 'next',
      chapter: 7,
      title: 'What should we make next? 🚀',
      body: 'Your practice board is yours to keep. Pick a prompt to leave in Copilot, then make it your own. You can replay this tour from Tutorials anytime.',
      choices: [
        { label: 'A carousel from my brief', to: 'next-carousel' },
        { label: 'A week of content ideas', to: 'next-week' },
        { label: 'A reel script I can film', to: 'next-script' },
        { label: 'Finish here', to: 'finish' },
      ],
    },
    ...(
      [
        [
          'carousel',
          'Read “Your creative brief” on this board and make a 5-slide carousel from my strongest idea, with a caption. Keep it specific to my audience and use only facts from the brief.',
        ],
        [
          'week',
          'Read “Your creative brief” and the drafts on this board. Plan 5 distinct posts for next week, with a hook, format, and goal for each. Save the plan as notes in a group called “My content week”. Don’t publish or schedule anything.',
        ],
        [
          'script',
          'Read “Your creative brief” on this board. Turn the strongest idea into a 30-second reel script I can film. Save it as a Content Project on this board. Don’t publish or schedule anything.',
        ],
      ] as const
    ).map(([id, prompt]) => ({
      id: `next-${id}`,
      display: 'hidden' as const,
      title: 'Your next idea is ready ✨',
      body: 'Make the prompt yours, then send it when you’re ready.',
      do: { action: 'copilot.fill' as const, params: { prompt }, then: 'finish' },
    })),
    {
      id: 'finish',
      display: 'hidden',
      title: 'Your playground is yours 🎉',
      body: 'Keep creating. Poppy’s right here when you need her.',
      do: { action: 'app.celebrate', then: 'end' },
    },
  ]),
};
